import React, { FC, useContext } from "react";
import { BrowserRouter } from "react-router-dom";
import awsConfig from "./aws-config/aws-config.json";
import "@aws-amplify/ui-react/styles.css";

import {
  Button,
  Group,
  Text,
  MantineProvider,
  MantineThemeOverride,
  Stack,
} from "@mantine/core";
import { ContextModalProps, ModalsProvider } from "@mantine/modals";

import { EnvConfig, GetEnv } from "./utils/EnvConfig";
import AppProvider, { AppContext } from "./api/AppContext";
import ApolloClientProvider from "./api/ApolloClientContext";
import Router from "./navigation/";
import { UnsubAppShell } from "./modules/AppShell";
import { Amplify } from "aws-amplify";
import { NotificationsProvider } from "@mantine/notifications";
// import { ffLogoThin } from "./assets";
import NoAuthRoutes from "./navigation/NoAuthRoutes";
import { MdDelete } from "react-icons/md";
import CatalogProvider from "./api/context/CatalogContext";
import CatalogModalProvider from "./api/context/CatalogModalContext";
import {
  BrandingProvider,
  useBrandingContext,
} from "./api/context/BrandingContext";
import { IMantineColor, generateTextMantineColor } from "./utils/theme";
import { MESSAGES } from "./utils/constants";
import { LoaderOverlayProvider } from "./api/context/LoaderOverlayContext ";

const env = GetEnv() as EnvConfig;
let awsConfigObj = awsConfig[env.env];
Amplify.configure(awsConfigObj);

const DeleteConfirmationModal = ({
  context,
  id,
  innerProps,
}: ContextModalProps<{
  innerText?: string;
  itemName?: string;
  onDelete: () => void;
  onCancel?: () => void;
  labels?: { cancel?: string; confirm?: string };
}>) => (
  <Stack spacing={15}>
    <Text size="sm">
      {innerProps.innerText ||
        `${MESSAGES.DEL_CONFIRM_MSG} ${
          innerProps.itemName ? " " + innerProps.itemName : ""
        }?`}
    </Text>
    <Group position="apart" spacing={10}>
      <Button
        fullWidth
        mt="md"
        onClick={() => {
          context.closeModal(id);
          innerProps.onCancel && innerProps.onCancel();
        }}
        variant="outline"
      >
        {innerProps.labels?.cancel || "Cancel"}
      </Button>
      <Button
        fullWidth
        mt="md"
        onClick={() => {
          //dont want to hide previous modal open
          context.closeModal(id);
          innerProps.onDelete();
        }}
        color="red"
        leftIcon={<MdDelete size={21} />}
      >
        {innerProps.labels?.confirm || "Delete"}
      </Button>
    </Group>
  </Stack>
);

const MessageModal = ({
  context,
  id,
  innerProps,
}: ContextModalProps<{
  modalBody: string;
  innerText?: string;
  btnText?: string;
  onCancel?: () => void;
}>) => (
  <>
    <Text size="sm">{innerProps.innerText}</Text>
    <Button
      fullWidth
      mt="md"
      onClick={() =>
        innerProps.onCancel ? innerProps.onCancel() : context.closeModal(id)
      }
    >
      {innerProps.btnText || `Close`}
    </Button>
  </>
);

interface Props {
  children: React.ReactNode;
}
const MantineProviders: FC<Props> = (
  { children },
  theme: MantineThemeOverride | undefined
) => {
  const { isDarkMode } = useContext(AppContext);
  const { primaryColors, secondaryColors } = useBrandingContext();

  return (
    <MantineProvider
      theme={{
        ...theme,
        components: {
          Badge: {
            styles: (theme) => ({
              root: {
                color: `${theme.colors.secondaryTextColor[4]} !important`,
                backgroundColor: `${theme.colors.primary[8]} !important`,
              },
            }),
          },
          Button: {
            styles: (theme) => ({
              inner: {
                color: `${theme.colors.primaryTextColor[4]} !important`,
              },
            }),
          },
          // Title: {
          //   styles: (theme) => ({
          //     root: {
          //       color: theme.colors.primaryTextColor[4],
          //     },
          //   }),
          // },
          // Text: {
          //   styles: (theme) => ({
          //     root: {
          //       color: theme.colors.secondaryTextColor[4],
          //     },
          //   }),
          // },

          InputWrapper: {
            styles: (theme) => ({
              root: {
                position: "relative",
                "&:focus-within": {
                  label: {
                    transform: "translateY(-20px)",
                    "&::before": {
                      opacity: 1,
                    },
                  },
                },
                "&:disabled": {
                  label: {
                    color: "red !important",
                  },
                },
              },
              label: {
                fontSize: 11,
                position: "absolute",
                top: 10,
                left: 10,
                display: "block",
                transition: "0.15s",
                background: "transparent",
                zIndex: 1,
                padding: "0 3px",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  width: "100%",
                  left: 1,
                  height: 8,
                  zIndex: -1,
                  bottom: 0,
                },
              },
            }),
          },
          Input: {
            styles: (theme) => ({
              input: {
                borderColor: theme.colors.secondary[0],
                "&:focus": {
                  borderColor: theme.colors.primary[6],
                },
                ":disabled": {
                  color: theme.colors.secondaryTextColor[4],
                },
              },
            }),
          },
          Modal: {
            styles: (theme) => ({
              inner: {
                ".mantine-Modal-modal": {
                  padding: 0,
                },
              },

              header: {
                borderTopRightRadius: 4,
                borderTopLeftRadius: 4,
                color: theme.colors.primaryTextColor[6],
                backgroundColor: theme.colors.primary[6],
                margin: 0,
                padding: 10,
                borderBottom: `1px solid ${theme.colors.secondary[0]}`,
                ".mantine-Modal-close": {
                  color: theme.colors.primaryTextColor[6],
                  "&:hover": {
                    backgroundColor: theme.colors.primary[4],
                  },
                },
              },
              title: {
                flex: 1,
                fontWeight: 500,
                fontSize: 20,
                textAlign: "center",
                marginRight: 0,
                marginLeft: 28,
                color: theme.colors.primaryTextColor[4],
              },
              body: {
                borderBottomLeftRadius: 4,
                borderBottomRightRadius: 4,
                backgroundColor: theme.colors.secondary[4],
                color: theme.colors.secondaryTextColor[4],
                padding: 20,
                ".mantine-Paper-root ": {
                  overflow: "inherit",
                },
                ".mantine-Group-root": {
                  button: {
                    flex: 1,
                  },
                },
              },
              root: {
                size: "min(450px, 80vw)",
              },
              modal: {
                backgroundColor: "rgba(0,0,0,0)",
              },
            }),
          },
        },
        colors: {
          greenMachine: [
            "#EEFCF2",
            "#C4F1D2",
            "#A2E4B6",
            "#85D79E",
            "#6EC989",
            "#5ABB77",
            "#4DAB69",
            "#409B5B",
            "#348C4E",
            "#2A7F43",
          ],
          blueOxford: [
            "#00104F",
            "#000E47",
            "#000C3F",
            "#000B38",
            "#000A32",
            "#00092D",
            "#000926",
            "#000723",
            "#00061F",
            "#00051B",
          ],
          charcoal: [
            "#363636",
            "#303030",
            "#2A2A2A",
            "#262626",
            "#222222",
            "#1E1E1E",
            "#1B1B1B",
            "#181818",
            "#151515",
            "#121212",
          ],
          light: [
            "#fff",
            "#f8f8f8",
            "#f2f2f2",
            "#ddd",
            "#d1d1d1",
            "#bfbfbf",
            "#B3B3B3",
            "#ADADAD",
            "#9a9a9a",
            "#777",
          ],
          dark: [
            "#4d4d4d",
            "#404040",
            "#3f3f3f",
            "#3b3b3b",
            "#303030",
            "#2e2e2e",
            "#222222",
            "#1e1e1e",
            "#181818",
            "#121212",
          ],
          primary: primaryColors as IMantineColor,
          secondary: secondaryColors as IMantineColor,
          primaryTextColor: generateTextMantineColor(primaryColors, true),
          secondaryTextColor: generateTextMantineColor(secondaryColors, true),
        },
        primaryColor: primaryColors.length ? "primary" : "charcoal",
        primaryShade: 6,
        black: "#1b1b1b",
        colorScheme: "light", //getColorScheme(secondaryColors[6]), //isDarkMode ? "dark" : "light",
      }}
    >
      <NotificationsProvider>
        <ModalsProvider
          modals={{ delete: DeleteConfirmationModal, message: MessageModal }}
        >
          {children}
        </ModalsProvider>
      </NotificationsProvider>
    </MantineProvider>
  );
};

const App = () => {
  return (
    <React.Fragment>
      <BrowserRouter>
        <ApolloClientProvider
          uri={env.uri}
          wsUri={env.wsUri}
          authChildren={
            <BrandingProvider>
              <AppProvider env={env}>
                <LoaderOverlayProvider>
                  <CatalogProvider>
                    <MantineProviders>
                      <CatalogModalProvider>
                        <Router />
                      </CatalogModalProvider>
                    </MantineProviders>
                  </CatalogProvider>
                </LoaderOverlayProvider>
              </AppProvider>
            </BrandingProvider>
          }
          noAuthChildren={
            <BrandingProvider>
              <MantineProviders>
                <UnsubAppShell>
                  <NoAuthRoutes />
                </UnsubAppShell>
              </MantineProviders>
            </BrandingProvider>
          }
        />
      </BrowserRouter>
    </React.Fragment>
  );
};

export default App;
